import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import MarketingLayout, { useMLang } from "./MarketingLayout";
import api from "../../api";

/**
 * /heavenly/signup?plan=starter|pro
 * Self-serve trial signup. Collects slug + name + email (+ WhatsApp), then
 * POSTs /api/billing/checkout and redirects to the Stripe URL.
 */

const COPY = {
    en: {
        title: "Start your 14-day free trial",
        sub: "Three quick questions, then you'll be redirected to Stripe to pay the $299 setup fee. Your storefront is provisioned automatically the second payment clears.",
        labelName: "Restaurant name",
        placeholderName: "La Casita Dominicana",
        labelSlug: "Pick your URL",
        urlPrefix: "/r/",
        slugHelp: "3-32 lowercase letters/digits/hyphens. This is your public storefront URL.",
        labelEmail: "Owner email",
        labelWa: "WhatsApp (with country code)",
        waHelp: "This is where customer orders will land.",
        labelPlan: "Plan",
        planStarter: "Basic — $99/mo",
        planPro: "Pro — $189/mo",
        submit: "Continue to Stripe",
        submitting: "Creating checkout…",
        bullets: [
            "14 days free, then $99 or $189/mo",
            "$299 one-time setup (covers onboarding + AI menu photos)",
            "Cancel anytime from the billing portal",
        ],
        errorGeneric: "Something went wrong. Please try again.",
        errorSlugTaken: "That slug is already taken. Try another.",
        errorMissing: "Please fill in restaurant name, slug and email.",
    },
    es: {
        title: "Empieza tu prueba gratis de 14 días",
        sub: "Tres preguntas rápidas y te redirigiremos a Stripe para pagar el setup de $299. Tu tienda se crea automáticamente al confirmar el pago.",
        labelName: "Nombre del restaurante",
        placeholderName: "La Casita Dominicana",
        labelSlug: "Elige tu URL",
        urlPrefix: "/r/",
        slugHelp: "3-32 letras minúsculas, números o guiones. Esta es la URL pública de tu tienda.",
        labelEmail: "Email del propietario",
        labelWa: "WhatsApp (con código de país)",
        waHelp: "Aquí llegarán los pedidos de tus clientes.",
        labelPlan: "Plan",
        planStarter: "Básico — $99/mes",
        planPro: "Pro — $189/mes",
        submit: "Continuar a Stripe",
        submitting: "Creando checkout…",
        bullets: [
            "14 días gratis, luego $99 o $189/mes",
            "$299 setup único (incluye onboarding + fotos IA del menú)",
            "Cancela cuando quieras desde el portal de facturación",
        ],
        errorGeneric: "Algo salió mal. Intenta de nuevo.",
        errorSlugTaken: "Ese slug ya está en uso. Prueba otro.",
        errorMissing: "Completa nombre, slug e email.",
    },
};

const MarketingSignup = () => {
    const { lang } = useMLang();
    const t = COPY[lang];
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        slug: "",
        owner_email: "",
        whatsapp_number: "",
        plan: params.get("plan") === "pro" ? "pro" : "starter",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Auto-derive slug from name when slug is untouched
    const [slugManual, setSlugManual] = useState(false);
    useEffect(() => {
        if (slugManual) return;
        const auto = form.name
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9-]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 32);
        setForm((f) => ({ ...f, slug: auto }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.name]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.name.trim() || !form.slug.trim() || !form.owner_email.includes("@")) {
            setError(t.errorMissing);
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/billing/subscription/checkout", {
                slug: form.slug.toLowerCase().trim(),
                name: form.name.trim(),
                plan: form.plan,
                owner_email: form.owner_email.trim(),
                whatsapp_number: form.whatsapp_number.trim() || null,
                origin_url: window.location.origin,
            });
            if (r.data?.url) {
                window.location.assign(r.data.url);
                return;
            }
            setError(t.errorGeneric);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) setError(t.errorSlugTaken);
            else if (typeof detail === "string") setError(detail);
            else setError(t.errorGeneric);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MarketingLayout testid="marketing-signup">
            <section className="max-w-5xl mx-auto px-6 sm:px-10 pt-12 sm:pt-16 pb-20 grid lg:grid-cols-5 gap-10">
                {/* Form column */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="lg:col-span-3"
                >
                    <h1 className="font-serif font-black text-4xl sm:text-5xl tracking-tight">{t.title}</h1>
                    <p className="text-white/65 mt-5 leading-relaxed">{t.sub}</p>

                    <form onSubmit={submit} className="mt-9 space-y-5">
                        <label className="block">
                            <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">{t.labelName}</div>
                            <input
                                data-testid="signup-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={t.placeholderName}
                                className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/15 focus:border-orange-400/60 focus:ring-2 focus:ring-orange-400/20 outline-none text-white placeholder:text-white/30"
                                required
                                maxLength={120}
                            />
                        </label>

                        <label className="block">
                            <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">{t.labelSlug}</div>
                            <div className="flex items-stretch rounded-xl overflow-hidden border border-white/15 focus-within:border-orange-400/60 focus-within:ring-2 focus-within:ring-orange-400/20">
                                <span className="px-3 py-3 bg-white/[0.03] text-white/50 text-sm font-mono border-r border-white/10">
                                    {t.urlPrefix}
                                </span>
                                <input
                                    data-testid="signup-slug"
                                    value={form.slug}
                                    onChange={(e) => {
                                        setSlugManual(true);
                                        setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") });
                                    }}
                                    placeholder="lacasita"
                                    className="flex-1 px-4 py-3 bg-white/[0.05] outline-none text-white placeholder:text-white/30 font-mono text-sm"
                                    required
                                    minLength={3}
                                    maxLength={32}
                                />
                            </div>
                            <div className="text-[11px] text-white/40 mt-1.5">{t.slugHelp}</div>
                        </label>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">{t.labelEmail}</div>
                                <input
                                    data-testid="signup-email"
                                    type="email"
                                    value={form.owner_email}
                                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                                    placeholder="you@restaurant.com"
                                    className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/15 focus:border-orange-400/60 focus:ring-2 focus:ring-orange-400/20 outline-none text-white placeholder:text-white/30"
                                    required
                                />
                            </label>
                            <label className="block">
                                <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">{t.labelWa}</div>
                                <input
                                    data-testid="signup-whatsapp"
                                    value={form.whatsapp_number}
                                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                                    placeholder="18763929056"
                                    className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/15 focus:border-orange-400/60 focus:ring-2 focus:ring-orange-400/20 outline-none text-white placeholder:text-white/30"
                                />
                                <div className="text-[11px] text-white/40 mt-1.5">{t.waHelp}</div>
                            </label>
                        </div>

                        <label className="block">
                            <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">{t.labelPlan}</div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {[
                                    { id: "starter", label: t.planStarter },
                                    { id: "pro", label: t.planPro },
                                ].map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        data-testid={`signup-plan-${p.id}`}
                                        onClick={() => setForm({ ...form, plan: p.id })}
                                        className={`px-4 py-3 rounded-xl text-left border transition-colors ${
                                            form.plan === p.id
                                                ? "border-orange-400/70 bg-orange-500/10 text-white ring-1 ring-orange-400/30"
                                                : "border-white/15 bg-white/[0.03] text-white/70 hover:border-white/30"
                                        }`}
                                    >
                                        <div className="text-sm font-semibold">{p.label}</div>
                                    </button>
                                ))}
                            </div>
                        </label>

                        {error && (
                            <div data-testid="signup-error" className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-lg px-4 py-2.5">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="signup-submit-btn"
                            className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white font-semibold shadow-xl shadow-orange-900/30 transition-all hover:scale-[1.01]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    {t.submitting}
                                </>
                            ) : (
                                <>
                                    {t.submit}
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </motion.div>

                {/* Side bullets */}
                <aside className="lg:col-span-2">
                    <div className="lg:sticky lg:top-28 p-6 rounded-2xl border border-orange-400/30 bg-orange-500/[0.05]">
                        <div className="text-xs uppercase tracking-[0.18em] text-orange-300 font-bold mb-4">
                            {lang === "es" ? "Lo que incluye" : "What you get"}
                        </div>
                        <ul className="space-y-3 text-sm text-white/80">
                            {t.bullets.map((b) => (
                                <li key={b} className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-orange-300 mt-0.5 shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 pt-6 border-t border-white/10 text-sm text-white/60">
                            {lang === "es" ? "¿Quieres verlo primero?" : "Want to see it first?"}
                            <div className="mt-1">
                                <Link to="/r/delicias" className="text-orange-300 hover:text-orange-200 font-semibold">
                                    {lang === "es" ? "Abre el demo →" : "Open the demo →"}
                                </Link>
                            </div>
                        </div>
                    </div>
                </aside>
            </section>
        </MarketingLayout>
    );
};

export default MarketingSignup;
